import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  MenuItem,
  MenuList,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import download from 'downloadjs';
import { useHistory } from 'react-router';
import {
  Button,
  Heading,
  Input,
  Menu,
  Pagination,
  StatusBadge,
  Table,
} from '~/components';
import { PAGE_SIZE, ROUTER_PATHS } from '~/constants';
import { useCompanies, useDebounce, useQueryParams } from '~/hooks';
import { replaceParameters } from '~/utils/replaceParamaters';
import { getCompaniesXlsx } from '~/services';
import { updateUrlParams } from '~/utils/updateUrlParams';

export const Companies: React.VFC = () => {
  const queryParams = useQueryParams();
  const [search, setSearch] = useState('');
  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const toast = useToast();

  const history = useHistory();
  const location = useLocation();

  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      situacao: queryParams.get('situacao') ?? '',
      situacaoFaturamento: queryParams.get('situacaoFaturamento') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
      order: queryParams.get('order') ?? 'ASC',
    };
  });

  const { data, isLoading, isFetching, error, refetch } = useCompanies(
    Number(parametros.pagina),
    parametros.busca,
    'nm_grupoeconomico',
    parametros.order,
    {},
    Number(parametros.paginacao),
  );

  useDebounce(
    () => {
      setParametros({ ...parametros, busca: searchTmp, pagina: '1' });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({ ...parametros, pagina: pagina ?? '1' });
    updateUrlParams({ ...parametros, pagina: pagina ?? '1' }, history);
  }, [location.search]);
  function handleGroupOrder() {
    const order = parametros.order === 'ASC' ? 'DESC' : 'ASC';
    setParametros({ ...parametros, order, pagina: '1' });
    updateUrlParams({ ...parametros, order, pagina: '1' }, history);
  }

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getCompaniesXlsx(parametros.busca, 'nm_grupoeconomico', parametros.order)
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const handlePageSizeChange = (e: any) => {
    setParametros({ ...parametros, paginacao: e.target.value, pagina: '1' });
    updateUrlParams(
      { ...parametros, paginacao: e.target.value, pagina: '1' },
      history,
    );
  };

  return (
    <Box px="8" py="4">
      <Heading>Empresas</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={5}>
          <Flex align="center" gridGap={4}>
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              w={350}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              fontSize="15px"
              placeholder="Procure por nome, CNPJ ou grupo"
            />
            {isFetching && <Spinner />}
          </Flex>
          <Button
            disabled={isLoadingDownload || isLoading}
            onClick={handleDownload}
            color="white"
            background="gray.900"
            _hover={{
              bgColor: 'black',
            }}
            style={{ marginTop: '20px' }}
          >
            Exportar
            {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
          </Button>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados das empresas.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Razão Social</Th>
                  <Th>Código</Th>
                  <Th>CNPJ</Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleGroupOrder()}
                    >
                      Grupo{' '}
                      {parametros.order === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th>Estado</Th>
                  <Th>Cidade</Th>
                  <Th>Tipo</Th>
                  <Th>Status</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(company => (
                    <Tr key={company.id_empresa}>
                      <Td>
                        <Link
                          to={`/portal/admin/companies/${company.id_empresa}`}
                        >
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {company.nm_razaosocial || '-'}
                          </Text>
                        </Link>
                      </Td>
                      <Td>{company.cd_usuariosap || '-'}</Td>
                      <Td>
                        <Link to={ROUTER_PATHS.PORTAL.ADMIN.GROUPS}>
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {company.cnpjFormatado}
                          </Text>
                        </Link>
                      </Td>
                      <Td>
                        {company.grupoeconomico?.nm_grupoeconomico || '-'}
                      </Td>
                      <Td>{company.estado?.sg_estado || '-'}</Td>
                      <Td>{company.nm_cidade || '-'}</Td>
                      <Td>{company.isHeadOffice ? 'Matriz' : 'Filial'}</Td>
                      <Td>
                        <StatusBadge variant={company.status ? 'green' : 'red'}>
                          {company.isActive}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuList>
                            <Link
                              to={replaceParameters(
                                ROUTER_PATHS.PORTAL.ADMIN.COMPANY_DETAIL,
                                String(company.id_empresa),
                              )}
                            >
                              <MenuItem>Visualizar Detalhes</MenuItem>
                            </Link>
                            <Link
                              to={replaceParameters(
                                ROUTER_PATHS.PORTAL.ADMIN.COMPANY_FORM,
                                String(company.id_empresa),
                              )}
                            >
                              <MenuItem>Editar</MenuItem>
                            </Link>
                            {company?.id_grupoeconomico && (
                              <Link
                                to={`/portal/admin/groups/form?groupId=${company.id_grupoeconomico}`}
                              >
                                <MenuItem>Grupo Econômico</MenuItem>
                              </Link>
                            )}
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              <Pagination
                totalCountOfRegisters={data?.count || 0}
                currentPage={Number(parametros.pagina) ?? 1}
                onPageChange={_page => {
                  setParametros({ ...parametros, pagina: _page.toString() });
                  updateUrlParams(
                    { ...parametros, pagina: _page.toString() },
                    history,
                  );
                }}
                onPageSizeChange={handlePageSizeChange}
                registersPerPage={Number(parametros.paginacao) ?? 10}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
